import { Box, debounce, useTheme } from '@mui/material';
import {
  DataGridPremium,
  GRID_AGGREGATION_FUNCTIONS,
  isAutogeneratedRow,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
} from '@mui/x-data-grid-premium';
import { useCallback, useEffect, useMemo, useState } from 'react';
import PortfolioAnalysisToolbar from './PortfolioAnalysisToolbar';

import { daDK } from '@mui/x-data-grid/locales';

import PortfolioAnalysisServiceCard from '@/features/portfolio-analysis/components/PortfolioAnalysisServiceCard';
import { getAnalysisTypeIcon } from '@/features/portfolio-analysis/utils/analysis-type-helpers';
import { useTranslation } from 'react-i18next';
import CustomNoRowsOverlay from '../../../../components/datagrid/DataGridNoRowsOverlay';
import { usePAColumns } from '../../hooks/pa-columns';
import { getPresets } from '../../utils/pa-presets';
import { useLocalStorageJson } from '@/hooks/useLocalStorageJson';
import { isChildRow } from '@/features/portfolio-analysis/utils/datagrid-helpers';
import useSearchParams from '@/hooks/useStableSearchParams';

// Utils

const firstAlphabeticalAggregation = {
  // The `apply` method takes the values to aggregate and returns the aggregated value
  apply: (params) => {
    if (params.values.length === 0) {
      return params.values[0];
    }

    const sortedValue = params.values.sort((a = '', b = '') =>
      a.localeCompare(b),
    );

    return sortedValue[0];
  },
  // The `label` property defines the label displayed in the column header when this aggregation is being used.
  label: 'firstAlphabetical',
  // The `types` property defines which type of columns can use this aggregation function.
  // Here, we only want to propose this aggregation function for `string` columns.
  // If not defined, aggregation will be available for all column types.
  columnTypes: ['string'],
};

const PortfolioAnalysisDataGrid = ({
  data,
  chosenPreset = 0,
  setSelectedMeter,
  setMeterType,
  analysisType,
  isLoading,
}) => {
  const theme = useTheme();
  const { t } = useTranslation([
    `portfolio-analysis_${analysisType}`,
    'portfolio-analsysis_dashboard',
  ]);

  const [favorites, setFavorites] = useLocalStorageJson(
    'portfolio_analysis_favorites',
    {},
  );
  const [searchParams] = useSearchParams();
  const isFullscreen = searchParams.get('fullscreen') === 'true';
  // 1️⃣ derive instead of storing
  const transformedData = useMemo(() => {
    return {
      ...data,
      portfolio_analysis_list: data.portfolio_analysis_list.map(
        (item, index) => ({
          favorites: Boolean(favorites[item.metering_point_id]),
          ...item,
          id: index,
          average_weekday_night: item.average_weekday_night ?? null,
          inactive_active_ratio: item.inactive_active_ratio ?? null,
          meter_in_sync: item.meter_in_sync ?? true,
        }),
      ),
    };
  }, [data, favorites]);

  // 2️⃣ single source of truth for toggling

  const presets = useMemo(
    () => getPresets(data, t, analysisType),
    [data, t, analysisType],
  );

  const apiRef = useGridApiRef();

  const totalColumns = usePAColumns(
    transformedData,
    analysisType,
    presets,
    chosenPreset,
  );

  const [columns, setColumns] = useState(totalColumns);
  useEffect(() => {
    let firstColumns = presets[chosenPreset].columnGroupingModel.map(
      (group) => {
        return group.children[0]?.field;
      },
    );
    firstColumns.push('warnings');
    const chosenOrder = presets[chosenPreset].order;
    if (chosenOrder.length > 0) {
      let tempArray = [];
      //Reorder the columns
      presets[chosenPreset].order.forEach((field) => {
        const column = totalColumns.find((col) => col.field === field);
        if (column) {
          tempArray.push({
            ...column,
            cellClassName: firstColumns.includes(column.field)
              ? 'cell-left-border'
              : '',
          });
        }
      });
      //Manually add address to make grouping work
      if (chosenOrder.includes('__row_group_by_columns_group__')) {
        tempArray = [
          totalColumns.find((col) => col.field === 'matrikel_key'),
          ...tempArray,
        ];
        tempArray = [
          totalColumns.find((col) => col.field === 'address'),
          ...tempArray,
        ];
        tempArray = [
          totalColumns.find((col) => col.field === 'main_address'),
          ...tempArray,
        ];
      }
      const reordered = [
        ...tempArray.filter((item) => item.field === 'favorites'),
        ...tempArray.filter((item) => item.field !== 'favorites'),
      ];
      setColumns(reordered);
    }
  }, [chosenPreset, presets, totalColumns]);

  const [isFavoritesFiltered, setIsFavoritesFiltered] = useState(false);

  useEffect(() => {
    //apiRef.current.setState
    apiRef.current.setSortModel(presets[chosenPreset].sortingModel);
    apiRef.current.setRowGroupingModel(presets[chosenPreset].rowGrouping.model);

    let tempFilter = [...presets[chosenPreset].filterModel.items];
    if (isFavoritesFiltered)
      tempFilter.push({
        field: 'favorites',
        operator: 'is',
        value: true,
      });
    apiRef.current.setFilterModel({ items: tempFilter });
  }, [apiRef, chosenPreset, isFavoritesFiltered, presets]);

  useEffect(() => {
    apiRef.current.setColumnVisibilityModel(
      generateVisibilityModel(presets[chosenPreset].order, totalColumns),
    );
  }, [presets, chosenPreset, totalColumns, apiRef]);

  function generateVisibilityModel(order, totalColumns) {
    // Initialize visibility model with all columns set to false
    let columnVisibilityModel = {};
    totalColumns.forEach((column) => {
      if (column.field != '__row_group_by_columns_group__')
        columnVisibilityModel[column.field] = false;
    });

    // Set the columns in order array to true
    order.forEach((column) => {
      if (Object.prototype.hasOwnProperty.call(columnVisibilityModel, column)) {
        columnVisibilityModel[column] = true;
      }
    });
    return columnVisibilityModel;
  }
  useEffect(() => {
    const gridColumns = apiRef.current.getAllColumns().map((col) => col.field);

    if (!gridColumns.includes('__row_group_by_columns_group__')) return;

    apiRef.current.setPinnedColumns({
      left: ['favorites', 'type', 'address', '__row_group_by_columns_group__'],
    });
  }, [apiRef, columns]); // or use a more specific trigger if needed
  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      columns: {
        columnVisibilityModel: {
          ...generateVisibilityModel(presets[chosenPreset].order, totalColumns),
        },
      },

      sorting: {
        sortModel: [...presets[chosenPreset].sortingModel],
      },
      rowGrouping: {
        model: [...presets[chosenPreset].rowGrouping.model],
      },
      filter: {
        filterModel: {
          items: [...presets[chosenPreset].filterModel.items],
          quickFilterExcludeHiddenColumns: false,
        },
      },
      aggregation: {
        model: {},
      },
      density: 'compact',
    },
  });

  const handleCellClick = useCallback(
    (params) => {
      if (params.field !== 'favorites') return;

      if (params.rowNode?.children?.length) {
        /* -------- group row -------- */
        setFavorites((prev) => {
          // 1️⃣ compute with the *current* state
          const allFav = params.rowNode.children.every((id) => {
            const child = apiRef.current.getRow(id);
            return child && prev[child.metering_point_id];
          });

          // 2️⃣ build the next object
          const next = { ...prev };
          params.rowNode.children.forEach((id) => {
            const child = apiRef.current.getRow(id);
            if (child) next[child.metering_point_id] = !allFav;
          });
          return next;
        });
      } else {
        /* -------- single row -------- */
        setFavorites((prev) => {
          return {
            ...prev,
            [params.row.metering_point_id]: !prev[params.row.metering_point_id],
          };
        });
      }
    },
    [apiRef, setFavorites], // ← no `favorites` dep needed
  );

  const handleFavoritesFilterClick = (bool) => {
    if (bool) {
      setFilterModel((prev) => {
        return {
          items: [
            ...prev.items,
            {
              field: 'favorites',
              operator: 'is',
              value: true,
            },
          ],
        };
      });
    }
    if (!bool) {
      setFilterModel((prev) => {
        return {
          items: prev.items.filter((value) => value.field !== 'favorites'),
        };
      });
    }
  };
  const [expandedGroups, setExpandedGroups] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const HandleNodeClick_withSavedExpandState = useCallback(
    debounce((node) => {
      // Get current apiRef state
      const CurrentGridState = apiRef.current.state;
      // Dynamically identify the group headings that are currently visible by using the visibleRowsLookup object, and the fact that group headings start with "auto-generated-row-type/"
      const visibleGroups = Object.keys(
        CurrentGridState.visibleRowsLookup,
      ).filter((row) => row.startsWith('auto-generated-row-matrikel_key/'));

      // Store the identified group headings for reference
      const visibleGroupsWithoutPrefix = visibleGroups.map((row) =>
        row.replace('auto-generated-row-matrikel_key/', ''),
      );

      // To check if each group is currently expanded, check if the first row of each group is visible
      const firstRowOfEachGroup = visibleGroups.map(
        (groupId) => apiRef.current.getRowGroupChildren({ groupId })[0],
      );
      const firstRowVisibility = firstRowOfEachGroup.map(
        (row) => CurrentGridState.visibleRowsLookup[row],
      );

      // Build an array of the visible groups (without prefix), based on those for which which the first row is visible
      const visibleExpandedGroups = visibleGroupsWithoutPrefix.filter(
        (_, index) => firstRowVisibility[index],
      );

      // Pass this array of expanded groups to the state for the next render
      setExpandedGroups(visibleExpandedGroups);

      // Call the original trigger for a state update
      handleCellClick(node);
    }, 100),
    [],
  );

  const expandAllGroups = useCallback(() => {
    const CurrentGridState = apiRef.current.state;

    // Get all visible group row IDs
    const visibleGroups = Object.keys(
      CurrentGridState.visibleRowsLookup,
    ).filter((row) => row.startsWith('auto-generated-row-matrikel_key/'));

    // Set each group to expanded
    visibleGroups.forEach((groupId) => {
      apiRef.current.setRowChildrenExpansion(groupId, true);
    });

    // Optionally update your saved expandedGroups state
    const visibleGroupsWithoutPrefix = visibleGroups.map((row) =>
      row.replace('auto-generated-row-matrikel_key/', ''),
    );
    setExpandedGroups(visibleGroupsWithoutPrefix);
  }, [apiRef, setExpandedGroups]);

  const handleExpandCollapse = () => {
    if (expandedGroups.length === 0) {
      expandAllGroups();
    } else {
      setExpandedGroups([]);
    }
  };
  const isGroupExpandedByDefault = useCallback(
    (node) =>
      node.groupingField === 'matrikel_key' &&
      expandedGroups.includes(String(node.groupingKey)),
    [expandedGroups],
  );
  const [filterModel, setFilterModel] = useState(
    presets[chosenPreset].filterModel,
  );
  const [rowGroupingModel, setRowGroupingModel] = useState(
    presets[chosenPreset].rowGrouping.model,
  );

  const serviceCardProps = {
    label: t(`cards.${analysisType}.title`, {
      ns: 'portfolio-analysis_dashboard',
    }),
    value: transformedData.portfolio_analysis_list.reduce((acc, o) => {
      if (o.type_of_mp === 'PV') {
        return acc; // skip solar meters
      }
      return o.total_consumption_year ? acc + o.total_consumption_year : acc;
    }, 0),
    unit: t(`cards.${analysisType}.unit`, {
      ns: 'portfolio-analysis_dashboard',
    }),
    icon: getAnalysisTypeIcon(analysisType),
    color: theme.palette[analysisType].main,
    dataPct: (() => {
      const { sum, count } = transformedData.portfolio_analysis_list.reduce(
        (acc, o) => {
          const v = o.one_year_data_pct;
          if (Number.isFinite(v)) {
            acc.sum += v;
            acc.count += 1;
          }
          return acc;
        },
        { sum: 0, count: 0 },
      );
      return count ? sum / count : 0; // mean
    })(),
    area: transformedData.portfolio_analysis_list.reduce(
      (acc, o) => (o.SumBuildingArea ? acc + o.SumBuildingArea : acc),
      0,
    ),
    buildingCount: new Set(
      transformedData.portfolio_analysis_list.map((item) => item.main_address),
    ).size,
    summedData: transformedData?.summed_data ?? [],
  };
  return (
    <>
      <Box my={!isFullscreen ? 2 : 1} width={'100%'}>
        {!isFullscreen && (
          <PortfolioAnalysisServiceCard {...serviceCardProps} />
        )}
      </Box>

      <DataGridPremium
        localeText={daDK.components.MuiDataGrid.defaultProps.localeText}
        slots={{
          toolbar: PortfolioAnalysisToolbar,
          noRowsOverlay: () => <CustomNoRowsOverlay rows={t('noRows')} />,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            handleFavoritesFilterClick: handleFavoritesFilterClick,
            isFavoritesFiltered: isFavoritesFiltered,
            setIsFavoritesFiltered: setIsFavoritesFiltered,
            analysisType: analysisType,
            handleExpandCollapse: () => {
              handleExpandCollapse();
            },
            isCollapsed: expandedGroups.length === 0,
            filterModel: filterModel,
          },
          loadingOverlay: {
            variant: 'linear-progress',
            noRowsVariant: 'skeleton',
          },
        }}
        loading={isLoading}
        getRowClassName={(params) =>
          params.row.type_of_mp === 'PV' ? 'solar-row' : ''
        }
        rows={transformedData.portfolio_analysis_list}
        columns={columns}
        showCellVerticalBorder={false}
        showColumnVerticalBorder={true}
        pageSize={5}
        columnGroupingModel={presets[chosenPreset].columnGroupingModel}
        apiRef={apiRef}
        aggregationFunctions={{
          ...GRID_AGGREGATION_FUNCTIONS,
          firstAlphabetical: firstAlphabeticalAggregation,
        }}
        getAggregationPosition={() => 'inline'}
        isGroupExpandedByDefault={isGroupExpandedByDefault}
        rowGroupingModel={rowGroupingModel}
        onRowGroupingModelChange={(newModel) => setRowGroupingModel(newModel)}
        filterModel={filterModel}
        onFilterModelChange={(newModel) => setFilterModel(newModel)}
        initialState={initialState}
        onCellClick={HandleNodeClick_withSavedExpandState}
        onRowClick={(params, e) => {
          if (e.target.nodeName === 'path' || e.target.nodeName === 'svg')
            return; //This is the best solution I could come up with for preventing drawer opening when clicking favorites button.
          if (typeof params.id === 'string' || params.id instanceof String) {
            setMeterType('matrikel');
            setSelectedMeter(
              transformedData.portfolio_analysis_list.find(
                (o) => o.matrikel_key == params.id.split('/')[1],
              ),
            );
          } else {
            const row = apiRef.current.getRow(params.id);
            const isLeaf =
              transformedData.portfolio_analysis_list.filter(
                (o) => o.matrikel_key == row.matrikel_key,
              ).length == 1;
            if (isLeaf) {
              setMeterType('matrikelMeter');
            } else {
              setMeterType('meter');
            }
            setSelectedMeter(params.row);
          }
        }}
        columnHeaderHeight={70}
        columnGroupHeaderHeight={56}
        sortingOrder={['desc', 'asc', null]}
        getCellClassName={(params) => {
          if (
            isAutogeneratedRow(params.row) &&
            params.field === '__row_group_by_columns_group__'
          ) {
            return 'autogenerated';
          }
          if (
            !isAutogeneratedRow(params.row) &&
            isChildRow(params) &&
            params.field === '__row_group_by_columns_group__'
          ) {
            return 'left-extra-margin-cell';
          }
          if (
            !isAutogeneratedRow(params.row) &&
            !isChildRow(params) &&
            params.field === '__row_group_by_columns_group__'
          ) {
            return 'left-margin-cell';
          }
          return '';
        }}
        groupingColDef={{
          width: 350,
          leafField: 'address',

          valueFormatter: (value) => {
            const list = transformedData.portfolio_analysis_list.filter(
              (item) => {
                return item.matrikel_key === value;
              },
            );
            return list.length > 0 ? list[0].main_address : value;
          },
          sortComparator: (date1, date2, cellParams1, cellParams2) => {
            let value1 = date1;
            let value2 = date2;

            let list1 = transformedData.portfolio_analysis_list.filter(
              (item) => item.matrikel_key === date1,
            );
            let list2 = transformedData.portfolio_analysis_list.filter(
              (item) => item.matrikel_key === date2,
            );
            if (list1.length > 0) value1 = list1[0].main_address;
            if (list2.length > 0) value2 = list2[0].main_address;

            if (
              cellParams1.rowNode.type !== 'group' ||
              cellParams2.rowNode.type !== 'group'
            ) {
              const api = cellParams1.api;
              const comparableDate1 =
                value1 ||
                (cellParams1.rowNode.children
                  ? api.getRow(cellParams1.rowNode.children[0])?.main_address
                  : undefined);
              const comparableDate2 =
                value2 ||
                (cellParams2.rowNode.children
                  ? api.getRow(cellParams2.rowNode.children[0])?.main_address
                  : undefined);
              if (!comparableDate1 || !comparableDate2) {
                return 0;
              }
              // Ascending order: return 1 if 1 > 2, -1 if 1 < 2
              return comparableDate1 > comparableDate2 ? -1 : 1;
            }
            // For group rows, compare directly
            return value1 > value2 ? -1 : 1;
          },
        }}
        sx={{
          '.MuiDataGrid-aggregationColumnHeaderLabel': {
            display: 'none',
          },
          '& .cell-left-border': {
            borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
          },
          '& .left-margin-cell': {
            paddingLeft: 7,
          },
          '& .left-extra-margin-cell': {
            paddingLeft: 9,
          },
          '& .autogenerated': {
            fontWeight: 'bold',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
          },
          '& .MuiDataGrid-columnHeaderTitleContainerContent': {
            height: '100%',
          },
          '&.MuiDataGrid-root .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainer':
            {
              pl: 1,
            },
          border: 'none',
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
            {
              outline: 'none',
            },
          '& .MuiDataGrid-root': {
            border: 0,
          },
          '& .MuiDataGrid-footerContainer': {
            border: 0,
          },
          '& .MuiDataGrid-bottomContainer': {
            border: 0,
          },
          '& .MuiDataGrid-main': {
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? '#fff' : '#121212',
            borderRadius: 2,
            border: (theme) => `1px solid ${theme.palette.divider}`,
          },
          '& .selected-row': {
            backgroundColor: (theme) => theme.palette.action.selected,
            '&:hover': {
              backgroundColor: (theme) => theme.palette.action.selected,
            },
          },
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },
          '& .solar-row .MuiDataGrid-cell': {
            color: '#00c853', // MUI green[600]
          },
          '--DataGrid-cellOffsetMultiplier': 1,
        }}
      />
    </>
  );
};

export default PortfolioAnalysisDataGrid;
